import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { alertOnFailure, toast } from "@redotech/redo-web/alert";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";

export const NotifyModal = memo(function NotifyModal({
  isOpen,
  onClose,
  productId,
  variantId,
  totalWaiting,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  variantId: string;
  totalWaiting: number;
  onSuccess: () => void;
}) {
  const [oldInventory, setOldInventory] = useState<number>(0);
  const [newInventory, setNewInventory] = useState<number>(10);
  const [validationError, setValidationError] = useState<string>();

  const notifyMutation = useRpcMutation(RedoMarketingRpcClientContext, {
    method: "triggerBackInStockNotifications",
  });

  const handleConfirm = async () => {
    if (newInventory <= oldInventory) {
      setValidationError(
        "New inventory must be greater than previous inventory",
      );
      return;
    }
    setValidationError(undefined);

    await alertOnFailure("Failed to trigger notifications")(async () => {
      const result = await notifyMutation.mutateAsync({
        productId,
        variantId,
        oldInventoryCount: oldInventory,
        newInventoryCount: newInventory,
      });

      toast(
        `Successfully triggered ${result.notificationCount} notification${result.notificationCount === 1 ? "" : "s"}`,
        { variant: "success" },
      );
      onClose();
      onSuccess();
    });
  };

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="md"
      onModalCloseRequested={() => {
        onClose();
        setValidationError(undefined);
      }}
      primaryButton={{
        text: "Confirm & Send",
        onClick: handleConfirm,
        disabled:
          newInventory == null ||
          oldInventory == null ||
          notifyMutation.isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={`Notify ${totalWaiting} waiting subscriber${totalWaiting === 1 ? "" : "s"}`}
      title="Notify all waiting subscribers"
    >
      <Flex dir="column" gap="lg">
        <Flex align="center" dir="row" gap="md" justify="space-around">
          <RedoIncrementDecrement
            label="Previous inventory"
            min={0}
            placeholder="0"
            setValue={(value) => setOldInventory(value)}
            value={oldInventory}
          />
          <RedoIncrementDecrement
            label="New inventory"
            min={1}
            placeholder="10"
            setValue={(value) => setNewInventory(value)}
            value={newInventory}
          />
        </Flex>
        <Text fontSize="sm" textColor="secondary">
          These inventory numbers determine which BIS flows will trigger based
          on their minimum inventory threshold settings. They will also be used
          to populate the corresponding variables in flow conditions and
          email/SMS templates.
        </Text>
        {validationError && (
          <Text fontSize="sm" textColor="error">
            {validationError}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
});
